const HEX_REGEX = /^#(?<hex>[0-9a-f]{6}|[0-9a-f]{8})$/i;

interface RGB {
  r: number;
  g: number;
  b: number;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const hexToRgb = (input: string): RGB | null => {
  const match = HEX_REGEX.exec(input.trim());
  if (!match?.groups?.hex) {
    return null;
  }

  const hex = match.groups.hex.slice(0, 6);
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) {
    return null;
  }
  return { r, g, b } as RGB;
};

const srgbToLinear = (value: number) => {
  const srgb = value / 255;
  return srgb <= 0.04045 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
};

export const relativeLuminance = (color: string): number => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return 0;
  }

  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (foreground: string, background: string): number => {
  const l1 = relativeLuminance(foreground);
  const l2 = relativeLuminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

const mixChannel = (channel: number, target: number, amount: number) =>
  clamp(Math.round(channel + (target - channel) * amount), 0, 255);

const rgbToHex = ({ r, g, b }: RGB) =>
  `#${[r, g, b]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
    .toLowerCase()}`;

export const lightenColor = (color: string, amount: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return color;
  }
  return rgbToHex({
    r: mixChannel(rgb.r, 255, amount),
    g: mixChannel(rgb.g, 255, amount),
    b: mixChannel(rgb.b, 255, amount),
  });
};

export const darkenColor = (color: string, amount: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return color;
  }
  return rgbToHex({
    r: mixChannel(rgb.r, 0, amount),
    g: mixChannel(rgb.g, 0, amount),
    b: mixChannel(rgb.b, 0, amount),
  });
};

export const inferColorScheme = (background: string): 'light' | 'dark' =>
  relativeLuminance(background) < 0.33 ? 'dark' : 'light';
